/**
 * CreditsChip — sidebar credits balance + Stripe top-up entry point.
 * Red state at zero balance pauses Claire/Auto Pilot (warning shown).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Plus, Crown } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreditsChip({ collapsed }) {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/credits/balance`).then((r) => {
      setData(r.data);
      const baseline = Math.max(r.data.baseline || 0, 1);
      const pct = Math.round((r.data.balance / baseline) * 100);
      if (!r.data.unlimited && pct < 20 && r.data.balance > 0
          && !sessionStorage.getItem("al_low_credits_warned")) {
        sessionStorage.setItem("al_low_credits_warned", "1");
        toast.error(isPt
          ? `⛽ Seu combustível está acabando! Você tem menos de 20% dos seus créditos (${r.data.balance}). Recarregue agora para manter a Claire ativa.`
          : `⛽ You're running on fumes! Less than 20% of your credits left (${r.data.balance}). Top up now to keep Claire running.`,
          {
            duration: 12000,
            action: {
              label: isPt ? "Recarregar" : "Top up",
              onClick: () => (window.location.href = "/billing/history"),
            },
          });
      }
    }).catch(() => {});
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  if (!data) return null;
  const zero = data.balance <= 0 && !data.unlimited;

  if (data.unlimited) {
    if (collapsed) {
      return (
        <div className="px-2 py-1.5 flex justify-center" title={isPt ? "Master — créditos ilimitados" : "Master — unlimited credits"}>
          <Crown className="w-4 h-4 text-[#C9A24E]" />
        </div>
      );
    }
    return (
      <div className="px-3 pb-2" data-testid="credits-chip">
        <div className="rounded-lg border border-[#C9A24E]/60 bg-gradient-to-r from-[#C9A24E]/15 to-[#C9A24E]/5 p-2.5"
          data-testid="credits-unlimited">
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 shrink-0 text-[#C9A24E]" />
            <span className="flex-1 text-[12px] font-extrabold text-[#C9A24E] cursor-pointer"
              onClick={() => (window.location.href = "/billing/history")}
              title={isPt ? "Ver histórico de uso" : "View usage history"}>
              ∞ {isPt ? "créditos" : "credits"}
            </span>
            <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#C9A24E] text-[#1A1A2E]">
              Master
            </span>
          </div>
          <p className="mt-1 text-[10px] leading-snug text-[#C9A24E]/85">
            {isPt
              ? "Todas as ferramentas liberadas — Claire e Auto Pilot sempre ativos."
              : "All tools unlocked — Claire & Auto Pilot always on."}
          </p>
        </div>
      </div>
    );
  }

  const pct = Math.max(0, Math.min(100, Math.round((data.balance / Math.max(data.baseline || 0, 1)) * 100)));
  const barColor = pct >= 50 ? "#10B981" : pct >= 20 ? "#F59E0B" : "#EF4444";

  if (collapsed) {
    return (
      <div className="px-2 py-1.5 flex justify-center" title={`${data.balance} ${isPt ? "créditos" : "credits"} · ${pct}%`}>
        <Coins className={`w-4 h-4 ${zero ? "text-rose-400" : "text-[#C9A24E]"}`} />
      </div>
    );
  }

  return (
    <div className="px-3 pb-2" data-testid="credits-chip">
      <div className={`rounded-lg border p-2.5 ${zero ? "border-rose-500/50 bg-rose-500/10" : "border-white/10 bg-white/[0.04]"}`}>
        <div className="flex items-center gap-2">
          <Coins className={`w-4 h-4 shrink-0 ${zero ? "text-rose-400" : "text-[#C9A24E]"}`} />
          <span className="flex-1 text-[12px] font-bold text-white/85 tabular-nums cursor-pointer hover:text-[#C9A24E]" data-testid="credits-balance"
            onClick={() => (window.location.href = "/billing/history")}
            title="Ver histórico de créditos">
            {data.balance.toLocaleString()} <span className="font-medium text-white/50">{isPt ? "créditos" : "credits"}</span>
          </span>
          <span className="text-[10px] font-bold tabular-nums" style={{ color: barColor }} data-testid="credits-pct">
            {pct}%
          </span>
        </div>
        {/* Progress bar — green / yellow (<50%) / red (<20%) */}
        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="credits-progress-track">
          <div className="h-full rounded-full transition-all duration-500"
            data-testid="credits-progress-bar"
            style={{ width: `${pct}%`, background: barColor }} />
        </div>
        {zero && (
          <p className="mt-1.5 text-[10px] leading-snug text-rose-300" data-testid="credits-zero-warning">
            {isPt
              ? "Saldo insuficiente — Claire e Auto Pilot pausados. Faça um top-up ou aguarde a renovação do plano."
              : "Insufficient balance — Claire & Auto Pilot paused. Top up or wait for plan renewal."}
          </p>
        )}
        <button type="button"
          onClick={() => (window.location.href = "/billing/history")}
          data-testid="credits-recharge-btn"
          className={`mt-2 w-full h-8 rounded-lg text-[11px] font-extrabold inline-flex items-center justify-center gap-1.5 transition ${
            pct < 20
              ? "bg-rose-500 text-white hover:bg-rose-400"
              : "bg-[#C9A24E] text-[#1A1A2E] hover:brightness-110"}`}>
          <Plus className="w-3 h-3" /> {isPt ? "Recarregar créditos" : "Top up credits"}
        </button>
      </div>
    </div>
  );
}
